<template>
    <modal-side-generic
    sizeClass="big"
    :headerColor="headerColor"
    :applyErrorState="applyErrorState"
    :title="selectedBatchFile.transferDescription"
    :subTitle="amountFormat(Math.abs(selectedBatchFile.totalAmount), selectedBatchFile.currency)"
    :subTitleIcon="headerTitleIcon"
    :icon="headerBigIcon"
    :buttonsTopData="selectedBatchFile.hasReceipt ? buttonsTopData : null"
    @hideMe="emit('hideMe')">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- Error State -->
		<empty-state-error v-if="applyErrorState && !showLoader" :message="t('genericErrorMessage')"/>
        <!-- Detail info -->
        <div class="batchfile-details" v-if="!showSpinner && !applyErrorState">
            <div
            v-for="(lineGroup, index) in parsedDetail"
            :key="`lineGroup${lineGroup.index}`"
            class="group-wrapper">
                <divider-generic :icon="lineGroup.icon" :title="lineGroup.label" :applyBorder="index !== 0"/>
                <!-- Text Block for each line -->
                <text-block-generic
                v-for="(line, index) in lineGroup.lines"
                applyEllipsis
                :key="`lineGroupLine${line,index}`"
                :params="line"
                :icon="line.icon"
                :colorIconPreset="line.colorIconPreset"
                :mask="line.mask"/>
                <!-- Buttons -->
                <div class="buttons-wrapper" v-if="lineGroup.buttons">
                    <button-tertiary
                    v-for="(button, index) in lineGroup.buttons"
                    :key="`lineGroupButton${button,index}`"
                    class="button"
                    :label="button.text"
                    :icon="button.icon"
                    @clicked="button.clickFn"/>
                </div>
                <!-- Table -->
                <table-with-horizontal-scroll
                v-if="lineGroup.table"
                noBoxStyles
                noHoverStyles
                rowHeightClass="small"
                :headerData="lineGroup.table.headerTable"
                :tableData="lineGroup.table.tableData"
                :tableStyles="lineGroup.table.tableStyles"
                @moreOptionsClicked="moreOptionsClicked" />
            </div>
        </div>
        <!-- <div v-if="detailInfo?.customerOperation?.favoritable" class="favorite-button-wrapper">
            <button-tertiary
            :key="favoriteButtonOptions.icon"
            :label="favoriteButtonOptions.label"
            :icon="favoriteButtonOptions.icon"
            @clicked="showFavoritesModal = true" />
        </div> -->
    </modal-side-generic>
    <!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    @hideMe="showFeedbackToast = false"/>
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendEmailInfoModal || showSendSMSInfoModal"
    :emailAction="sendEmailAction"
    smsAction="sendBatchfileEntrySMSNotification"
    :sendType="showSendEmailInfoModal ? 'email' : 'sms'"
    :payload="sendPayload"
    @hideMe="[showSendEmailInfoModal = false, showSendSMSInfoModal = false]"/>
    <!-- Component to handle download logic-->
	<download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
    <!-- Share Link Modal -->
    <modal-centered-share-link
    v-if="showShareLinkModal"
    :operationId="shareLinkOperationId.operationId"
    :payload="shareLinkOperationId.payload"
    @hideMe="showShareLinkModal = false"/>
    <!-- Favorites modal -->
    <favorites-modal
    v-if="showFavoritesModal"
    :modalParams="{ operationId: detailInfo.customerOperation?.id, type: favoriteButtonOptions.type }"
    @hideMe="showFavoritesModal = false"
    @requestSuccessful="favoriteModalSuccessHandler"
    @requestError="favoriteModalErrorHandler" />
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import TableWithHorizontalScroll from '@/components/generic/table/WithHorizontalScroll'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { iconMapper, colorStatusMapperModal, iconColorMapper } from '@/assets/scripts/mappers/batchFiles'
import { iconColorMapperDetailTable, iconMapperDetailTable } from '@/assets/scripts/mappers/batchfileTransfers'
import { sucessObjectConstrutor, errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createBatchfileEntryDownloadReceiptUrl, createBatchfileExcelDownloadUrl, createBatchfileDownloadReceiptUrl } from '@/network/api/batchfile.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestBatchfileTransferDetail } = mapActions(moduleMapper.PAYMENTS_BATCHFILE_CONSULT)

/* Emmitters */
const emit = defineEmits(['hideMe'])

const props = defineProps({
    selectedBatchFile: Array
})

/* Data */
const showSpinner = ref(false)
const detailInfo = ref(null)
const buttonsTopData = ref({})
const showSendEmailInfoModal = ref(false)
const showSendSMSInfoModal = ref(false)
const showShareLinkModal = ref(false)
const shareLinkOperationId = ref(null)
const downloadLink = ref(null)
const showFavoritesModal = ref(false)
const isFavorite = ref(false)
/*************************************************
*                                                *
*                Side Modal Info                 *
*                                                *
*************************************************/
const headerColor = computed(() => {
    return colorStatusMapperModal(props.selectedBatchFile.status)
})

const headerBigIcon = computed(() => {
    return iconMapper(props.selectedBatchFile.status)
})

const headerTitleIcon = computed(() => {
    if (props.selectedBatchFile.totalAmount === 0) return null
    return props.selectedBatchFile.totalAmount < 0 ? 'arrow-up-full' : 'arrow-down-full'
})

buttonsTopData.value = {
	downloadUrl: createBatchfileDownloadReceiptUrl(props.selectedBatchFile.id),
    shareLinkPayload: 'generateShareLink',
    emailAction: 'sendBatchfileEmailNotification',
    smsAction: 'sendBatchfileSMSNotification',
    sendPayload: { id: props.selectedBatchFile.id },
    operationId: props.selectedBatchFile.customerOperation?.id
}

const favoriteButtonOptions = computed(() => {
    if (isFavorite.value) {
        return {
            label: t('payments.history.detailModal.favoriteButtonOptions.removeFavorite'),
            icon: 'star-minus',
            type: 'remove'
        }
    } else {
        return {
            label: t('payments.history.detailModal.favoriteButtonOptions.saveAsFavorite'),
            icon: 'star',
            type: 'create'
        }
    }
})

/*************************************************
*                                                *
*               Batch Files Detail               *
*                                                *
*************************************************/
const parsedDetail = computed(() => {
    return [
        {
            label: t('payments.batchfile.consult.detailModal.fileInfo'),
            icon: 'info',
            lines: [{
                label: t('payments.batchfile.consult.detailModal.fileName'),
                content: detailInfo.value.originalFileName
            }, {
                label: t('payments.batchfile.consult.detailModal.totalAmount'),
                content: amountFormat(Math.abs(detailInfo.value.totalAmount), detailInfo.value.currency) || '-',
                icon: 'arrow-down-full',
                colorIconPreset: 'negative',
                classes: 'amount-icon'
            }, {
                label: t('payments.batchfile.consult.detailModal.entryCount'),
                content: detailInfo.value.entryCount
            }, {
                label: t('payments.batchfile.consult.detailModal.uploadDate'),
                content: getDateWithTime(detailInfo.value.uploadDate) || '-',
                classes: 'capitalize'
            }, {
                label: t('payments.batchfile.consult.detailModal.operationTypeHeader'),
                content: detailInfo.value.operationTypeHeader
            }, {
                label: t('payments.batchfile.consult.detailModal.status'),
                content: detailInfo.value.batchFileSubmissionStatusDescription,
                icon: iconMapper(detailInfo.value.status),
                colorIconPreset: iconColorMapper(detailInfo.value.status)
            }, {
                label: t('payments.batchfile.consult.detailModal.sourceReference'),
                content: detailInfo.value.sourceReference
            }],
            buttons: [{
                icon: 'download',
                text: t('payments.batchfile.consult.detailModal.downloadFile'),
                clickFn: () => {
                    downloadLink.value = createBatchfileExcelDownloadUrl(props.selectedBatchFile.id)
                }
            },
            {
                icon: 'letter',
                text: t('payments.batchfile.consult.detailModal.sendFileViaEmail'),
                clickFn: sendFileByEmailClickHandler
            }]
        },
        {
            label: t('payments.batchfile.consult.detailModal.payments'),
            icon: 'payment-single',
            table: {
                headerTable: [
                    { label: t('payments.batchfile.consult.detailModal.tableHeaderData.iban'), dataAttr: 'iban', rowCellType: 'title' },
                    { label: t('payments.batchfile.consult.detailModal.tableHeaderData.companyReference'), dataAttr: 'companyReference', rowCellType: 'description' },
                    { label: t('payments.batchfile.consult.detailModal.tableHeaderData.amount'), dataAttr: 'amount', rowCellType: 'amount' },
                    { label: t('payments.batchfile.consult.detailModal.tableHeaderData.transferReference'), dataAttr: 'transferReference', rowCellType: 'description' },
                    { label: t('payments.batchfile.consult.detailModal.tableHeaderData.email'), dataAttr: 'email', rowCellType: 'description' },
                    { label: t('payments.batchfile.consult.detailModal.tableHeaderData.registryStatusDescription'), dataAttr: 'status', rowCellType: 'status' },
                    { dataAttr: 'tableOptions', rowCellType: 'more-options' }
                ],
                tableStyles: [
                    { width: '225px', 'min-width': '225px' },
                    { width: '150px', 'min-width': '150px', 'text-align': 'end' },
                    { width: '150px', 'min-width': '150px', 'text-align': 'end' },
                    { width: '100px', 'min-width': '100px', 'text-align': 'end' },
                    { width: '100px', 'min-width': '100px', 'text-align': 'end' },
                    { width: '100px', 'min-width': '100px', 'text-align': 'end' },
                    null
                ],
                tableData: detailInfo.value.fileEntries.map(entry => {
                    const joinedValues = entry.nibBankCode && entry.nibBranchCode && entry.nibAccountNumber && entry.nibCheckDigit ? `AO06${entry.nibBankCode}${entry.nibBranchCode}${entry.nibAccountNumber}${entry.nibCheckDigit}` : ''

                    return [
                        { value: entry.sequenceNumber, dataAttr: 'id' },
                        { type: 'title', value: entry.iban || joinedValues, dataAttr: 'iban' },
                        { value: entry.companyReference, dataAttr: 'companyReference' },
                        { type: 'amount', value: { content: entry.amount, currency: entry.currency, noBalanceIcon: true }, dataAttr: 'amount' },
                        { value: entry.transferReference, dataAttr: 'transferReference' },
                        { value: entry.email || '-', dataAttr: 'email' },
                        { type: 'status', value: { text: entry.registryStatusDescription || '-', icon: iconMapperDetailTable(entry.registryStatus), classes: iconColorMapperDetailTable(entry.registryStatus) }, dataAttr: 'fileStatus' },
                        { type: 'more-options', value: moreOptions, dataAttr: 'tableOptions', hidden: entry.registryStatusDescription !== 'Sucesso' }
                    ]
                })
            }
        }
    ]
})

const sendPayload = ref({})
const sendEmailAction = ref(null)

const sendFileByEmailClickHandler = () => {
    sendPayload.value = {
        batchFileId: props.selectedBatchFile.id
    }
    sendEmailAction.value = 'sendBatchfileXLSEmail'
    showSendEmailInfoModal.value = true
}

const moreOptionsClicked = ev => {
    const sequenceNumber = ev.rowId
	moreOptions.find(obj => obj.id === ev.optionClicked).callbackFunction(props.selectedBatchFile.customerOperation?.id, props.selectedBatchFile.id, sequenceNumber)
}

const sendEmail = (customerOperationId, batchFileId, sequence) => {
    sendPayload.value = {
        id: batchFileId,
        sequence: sequence
    }
    sendEmailAction.value = 'sendBatchfileEntryEmailNotification'
    showSendEmailInfoModal.value = true
}

const sendSMS = (customerOperationId, batchFileId, sequence) => {
    sendPayload.value = {
        id: batchFileId,
        sequence: sequence
    }
    showSendSMSInfoModal.value = true
}

const downloadPDF = (customerOperationId, batchFileId, sequence) => {
    downloadLink.value = createBatchfileEntryDownloadReceiptUrl({ id: batchFileId, sequence: sequence })
}

const shareLink = (customerOperationId, batchFileId, sequence) => {
    shareLinkOperationId.value = {
        operationId: customerOperationId,
        payload: {
            dataParams: { sequence: sequence }
        }
    }
    showShareLinkModal.value = true
}

const moreOptions = [
	{
		id: 1,
		text: t('payments.batchfile.consult.moreOptions.sendEmail'),
		icon: 'letter',
		callbackFunction: sendEmail
	}, {
		id: 2,
		text: t('payments.batchfile.consult.moreOptions.sendSms'),
		icon: 'chat',
		callbackFunction: sendSMS
	}, {
		id: 3,
		text: t('payments.batchfile.consult.moreOptions.download'),
		icon: 'download',
		callbackFunction: downloadPDF
	}, {
		id: 4,
		text: t('payments.batchfile.consult.moreOptions.generateLink'),
		icon: 'link',
		callbackFunction: shareLink
	}
]

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestBatchfileDetails = async () => {
	showSpinner.value = true
    showFeedbackToast.value = false

    try {
        const response = await requestBatchfileTransferDetail(props.selectedBatchFile.id)
        detailInfo.value = response.data.data
        isFavorite.value = detailInfo.value.customerOperation?.favorite
        showSpinner.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error.response.data, requestBatchfileDetails)
    }
}

/*************************************************
*                                                *
*                 Favorite Modal                 *
*                                                *
*************************************************/
const favoriteModalSuccessHandler = event => {
    isFavorite.value = true
    showFavoritesModal.value = false
    sucessFeedbackToastHandler(event)
}

const favoriteModalErrorHandler = event => {
    errorHandler(event)
    showFavoritesModal.value = false
}

const sucessFeedbackToastHandler = data => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
    showFeedbackToast.value = true
}
/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	showFeedbackToast.value = true
    applyErrorState.value = true

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestBatchfileDetails()
</script>

<style lang="scss" scoped>
.buttons-wrapper {
    display: flex;
    gap: 16px;
}
.with-table {
    .left-divider-wrapper.apply-border {
        width: calc(100% - 24px);
        margin-left: 24px;
    }
}

</style>
